/**
 * Accounts (optional nickname + 4-digit PIN): a logged-in player finds their games again in
 * "My games", on any device. The login token is kept in localStorage.
 *
 * Without an account, this browser still remembers the last games it opened ("recent games"),
 * so "Return to my game" works too.
 */
import { safeLocalStorage } from './SafeLocalStorage';
import { paths } from '@/common/app/paths';
import { MAX_RECENT_GAMES, } from '@/common/accounts/AccountModel';
import { translateText, translateTextWithParams } from '@/client/directives/i18n';
const ACCOUNT_KEY = 'tfm.account';
const RECENT_KEY = 'tfm.recentGames';
export function currentAccount() {
    try {
        const value = JSON.parse(safeLocalStorage.getItem(ACCOUNT_KEY) ?? 'null');
        if (typeof value?.token === 'string' && typeof value?.nickname === 'string') {
            return value;
        }
    }
    catch {
        // Falls through.
    }
    return undefined;
}
function setAccount(account) {
    if (account === undefined) {
        safeLocalStorage.removeItem(ACCOUNT_KEY);
    }
    else {
        safeLocalStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
    }
}
/** A failed account request. The message is already translated. */
export class AccountRequestError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
async function call(method, path, body) {
    const account = currentAccount();
    const headers = { 'Content-Type': 'application/json' };
    if (account !== undefined) {
        headers['Authorization'] = `Bearer ${account.token}`;
    }
    let response;
    try {
        response = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    }
    catch {
        throw new AccountRequestError(translateText('Could not reach the server.'), 0);
    }
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
        if (response.status === 401 && account !== undefined && path !== paths.API_ACCOUNT_LOGIN) {
            // The login expired: log out on this browser too.
            setAccount(undefined);
        }
        const error = json;
        const message = typeof error.error === 'string' ?
            translateTextWithParams(error.error, error.params ?? []) :
            translateText('Something went wrong on the server.');
        throw new AccountRequestError(message, response.status);
    }
    return json;
}
async function signIn(path, nickname, pin) {
    const response = await call('POST', path, { nickname, pin });
    setAccount({ token: response.token, nickname: response.nickname });
    // Games this browser opened without an account join "My games" (not those of another account on this browser).
    const recent = recentGames();
    for (const game of recent.filter((r) => r.by === undefined)) {
        await addGame(game.id).catch(() => undefined);
        game.by = response.nickname;
    }
    setRecentGames(recent);
    return response.nickname;
}
export function login(nickname, pin) {
    return signIn(paths.API_ACCOUNT_LOGIN, nickname, pin);
}
export function register(nickname, pin) {
    return signIn(paths.API_ACCOUNT_REGISTER, nickname, pin);
}
export async function logout() {
    await call('POST', paths.API_ACCOUNT_LOGOUT).catch(() => undefined);
    setAccount(undefined);
}
/** The account's games. */
export function myGames() {
    return call('GET', paths.API_ACCOUNT_GAMES);
}
/** Adds a game (from a player, spectator or game id) to the account's games. */
export async function addGame(id) {
    await call('POST', paths.API_ACCOUNT_GAMES, { action: 'add', id });
}
export async function removeGame(gameId) {
    await call('POST', paths.API_ACCOUNT_GAMES, { action: 'remove', id: gameId });
}
/** Games this browser opened, most recent first. */
export function recentGames() {
    try {
        const value = JSON.parse(safeLocalStorage.getItem(RECENT_KEY) ?? '[]');
        if (Array.isArray(value)) {
            return value.filter((r) => typeof r?.id === 'string' && typeof r?.openedMs === 'number');
        }
    }
    catch {
        // Falls through.
    }
    return [];
}
function setRecentGames(games) {
    safeLocalStorage.setItem(RECENT_KEY, JSON.stringify(games.slice(0, MAX_RECENT_GAMES)));
}
/** Forgets a game on this browser. `ids` are every id that may stand for it. */
export function forgetRecentGame(ids) {
    setRecentGames(recentGames().filter((r) => !ids.includes(r.id)));
}
/** Descriptions of the games this browser opened. */
export async function recentGameSummaries() {
    const ids = recentGames().map((r) => r.id);
    if (ids.length === 0) {
        return [];
    }
    const response = await call('POST', paths.API_ACCOUNT_RECENT, { ids });
    const opened = new Map(recentGames().map((r) => [r.id, r.openedMs]));
    // The page with every link and a seat of the same game are one game: keep the seat.
    const rank = (g) => g.participantId === undefined ? 0 : g.participantId.startsWith('s') ? 1 : 2;
    const byGame = new Map();
    for (const game of response.games) {
        const ms = opened.get(game.participantId ?? game.gameId);
        if (ms !== undefined) {
            game.lastActivityMs = Math.max(game.lastActivityMs, ms);
        }
        const other = byGame.get(game.gameId);
        if (other === undefined || rank(game) > rank(other) || (rank(game) === rank(other) && game.lastActivityMs > other.lastActivityMs)) {
            byGame.set(game.gameId, game);
        }
    }
    return Array.from(byGame.values());
}
/**
 * Notes that this browser opened a game (a player link, the setup table, or the page with every link):
 * it joins the recent games and, when logged in, "My games".
 *
 * When both the game page (`g...`) and a seat of the same game were opened, the seat is shown.
 */
export function rememberGame(id) {
    const now = Date.now();
    const games = recentGames().filter((r) => r.id !== id);
    const account = currentAccount();
    games.unshift(account === undefined ? { id, openedMs: now } : { id, openedMs: now, by: account.nickname });
    setRecentGames(games);
    if (account === undefined) {
        return Promise.resolve();
    }
    return addGame(id).catch((e) => console.warn('Could not add the game to My games', e));
}
/** The id in a `player?id=...` style link. */
export function idOfLink(link) {
    const match = link.match(/[?&]id=([gps]z?[0-9a-f]+)/);
    return match === null ? undefined : match[1];
}
/** Where a "log in" link sends the player back to afterwards. */
export function loginHref(returnTo = window.location.pathname.replace(/^\//, '') + window.location.search) {
    return `${paths.ACCOUNT}?next=${encodeURIComponent(returnTo)}`;
}
/** Only relative links of this app are allowed as `next`, never another site. */
export function safeNext(next) {
    if (next === null || next === '' || !/^[a-z][a-z0-9-]*(\?[\w=&%.-]*)?$/i.test(next)) {
        return '/';
    }
    return next;
}
